import { format, parseISO } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const COLUMN_WIDTHS = {
  orderTaskNumber: 130,
  productCode: 130,
  productName: 120,
  specification: 150,
  orderQty: 60,
  plannedQty: 60,
  cumulativeCompleted: 70,
  orderPlan: 70,
  orderDate: 90,
  plannedEndDate: 90,
  plannedHours: 60,
  equipment: 90,
  date: 72,
};

export function getColumnWidths() {
  return COLUMN_WIDTHS;
}

function safeDate(value) {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatDateShort(value) {
  const d = safeDate(value);
  return d ? format(d, 'yyyy.M.d') : '';
}

export function groupSchedulesForExport({
  schedules,
  cumulativeTotalsMap,
  dates,
  workCenters,
}) {
  // `cumulativeTotalsMap` is computed by the backend across the entire schedule
  // history and keyed by the same order-item-step identifier used here.
  const totalsMap = cumulativeTotalsMap || {};
  const dateKeys = dates.map((d) => format(d, 'yyyy-MM-dd'));

  const getGroupKey = (s) => {
    const isCustom = s.source === 'custom' || !s.production_order_id;
    return isCustom
      ? `custom-${s.finished_product_code || ''}-${s.specification || ''}-${s.step_name || ''}-${s._id}`
      : `${s.production_order_id}-${s.item_id || ''}-${s.step_sequence || 0}`;
  };

  const createGroup = (s, key, wc) => {
    const isCustom = s.source === 'custom' || !s.production_order_id;
    const orderDate = isCustom
      ? (s.notes || formatDateShort(s.created_at))
      : formatDateShort(s.created_at);
    const plannedEndDate = isCustom ? formatDateShort(s.scheduled_date) : '';
    const orderPlan = isCustom ? 0 : (s.item_planned_quantity || 0);
    return {
      key,
      isCustom,
      orderTaskNumber: isCustom ? '临时新增' : (s.production_order_number || ''),
      productCode: s.finished_product_code || '',
      productName: s.finished_product_name || '',
      specification: s.specification || '',
      orderQty: orderPlan,
      plannedQty: orderPlan,
      cumulativeCompleted: 0,
      orderPlan,
      orderDate,
      plannedEndDate,
      plannedHours: 0,
      equipment: wc.name,
      workCenter: wc,
      scheduleIds: [],
      dateMap: Object.fromEntries(dateKeys.map((dk) => [dk, 0])),
      completedMap: Object.fromEntries(dateKeys.map((dk) => [dk, 0])),
      scrappedMap: Object.fromEntries(dateKeys.map((dk) => [dk, 0])),
      totalAssigned: 0,
      totalCompleted: 0,
      totalScrapped: 0,
    };
  };

  const rowsByWorkCenter = [];

  workCenters.forEach((wc) => {
    const wcId = wc._id;

    const displayed = schedules.filter((s) => {
      const swcId = s.work_center_id?._id || s.work_center_id;
      return swcId === wcId || s.work_center_name === wc.name;
    });
    if (displayed.length === 0) return;

    const groups = new Map();

    displayed.forEach((s) => {
      const key = getGroupKey(s);
      if (!groups.has(key)) {
        groups.set(key, createGroup(s, key, wc));
      }
      const group = groups.get(key);
      const dk = format(safeDate(s.scheduled_date), 'yyyy-MM-dd');
      const assignedQty = s.assigned_quantity || 0;
      const completedQty = s.completed_quantity || 0;
      const scrappedQty = s.scrapped_quantity || 0;
      group.scheduleIds.push(String(s._id));
      group.dateMap[dk] = (group.dateMap[dk] || 0) + assignedQty;
      group.completedMap[dk] = (group.completedMap[dk] || 0) + completedQty;
      group.scrappedMap[dk] = (group.scrappedMap[dk] || 0) + scrappedQty;
      group.totalAssigned += assignedQty;
      group.totalCompleted += completedQty;
      group.totalScrapped += scrappedQty;
      group.plannedHours = Math.max(group.plannedHours, s.standard_time || 0);

      if (group.isCustom) {
        group.orderQty = group.totalAssigned;
        group.plannedQty = group.totalAssigned;
        group.orderPlan = group.totalAssigned;
      }
    });

    const rows = Array.from(groups.values()).map((group) => {
      const backendTotal = totalsMap[group.key];
      return {
        ...group,
        cumulativeCompleted: backendTotal ? backendTotal.completed || 0 : 0,
      };
    });
    if (rows.length === 0) return;

    const groupTotals = rows.reduce(
      (acc, r) => ({
        totalAssigned: acc.totalAssigned + r.totalAssigned,
        totalCompleted: acc.totalCompleted + r.totalCompleted,
        totalScrapped: acc.totalScrapped + r.totalScrapped,
        totalPlannedQty: acc.totalPlannedQty + r.plannedQty,
      }),
      { totalAssigned: 0, totalCompleted: 0, totalScrapped: 0, totalPlannedQty: 0 }
    );

    rowsByWorkCenter.push({
      workCenter: wc,
      rows,
      summary: groupTotals,
    });
  });

  return { dateKeys, rowsByWorkCenter };
}

export function formatDayHeader(date) {
  return {
    date: format(date, 'M/d'),
    weekday: format(date, 'EEE', { locale: zhCN }),
  };
}

export function renderRestMarker(_date, value) {
  return value > 0 ? String(value) : '';
}

export function calculateAchievementRate(completed, assigned) {
  if (!assigned) return '0.0%';
  return `${((completed / assigned) * 100).toFixed(1)}%`;
}

export function calculateDefectRate(scrapped, completed) {
  const total = completed + scrapped;
  if (!total) return '0.0%';
  return `${((scrapped / total) * 100).toFixed(1)}%`;
}

/**
 * Compute the cumulative completed/scrapped quantity for a group of schedules
 * that belong to the same order-item-step.
 *
 * Each schedule stores the cumulative completed/scrapped quantity up to the day it
 * was settled. When a schedule is rolled over, the successor starts fresh (its
 * completed_quantity is 0). Therefore the total cumulative for the order-item-step
 * is the sum of completed_quantity / scrapped_quantity across every non-cancelled
 * schedule in the group, including rolled-over schedules and their successors.
 *
 * `allSchedules` is kept for API compatibility but no longer used for filtering.
 */
export function computeStepCumulative(groupSchedules, _allSchedules) {
  return groupSchedules.reduce(
    (acc, s) => {
      if (s.status === 'cancelled') return acc;
      acc.completed += s.completed_quantity || 0;
      acc.scrapped += s.scrapped_quantity || 0;
      return acc;
    },
    { completed: 0, scrapped: 0 }
  );
}

export function computeDailyGroupSummaries(
  rows,
  dates,
  settledDateMap = {},
  workCenterName = '',
  rolledInScheduleIds
) {
  // Items whose schedule was rolled over from a previous date should not count
  // toward the day's achievement rate (计划达成率) — only tasks originally
  // scheduled for that day do. Newer settlement records carry
  // `rolled_from_schedule_id` on each item; for older records we fall back to
  // the schedule lookup set built from the board's schedule list.
  const isRolledIn = (item) =>
    Boolean(item.rolled_from_schedule_id) ||
    (rolledInScheduleIds && rolledInScheduleIds.has(String(item.schedule_object_id)));

  return dates.map((date) => {
    const dk = format(date, 'yyyy-MM-dd');
    const settlement = settledDateMap[dk];
    const isSettled = Boolean(
      settlement && Array.isArray(settlement.items)
    );

    if (isSettled) {
      // Use the official settlement record so exported stats reflect
      // the actual daily completion / scrap numbers, not cumulative values.
      const items = settlement.items.filter(
        (item) =>
          !workCenterName ||
          (item.work_center_name || '').trim() === workCenterName.trim()
      );
      const totalAssigned = items.reduce(
        (sum, item) => sum + (item.assigned_quantity || 0),
        0
      );
      const totalCompleted = items.reduce(
        (sum, item) => sum + (item.completed_quantity || 0),
        0
      );
      const totalScrapped = items.reduce(
        (sum, item) => sum + (item.scrapped_quantity || 0),
        0
      );
      const freshItems = items.filter((item) => !isRolledIn(item));
      const freshAssigned = freshItems.reduce(
        (sum, item) => sum + (item.assigned_quantity || 0),
        0
      );
      const freshCompleted = freshItems.reduce(
        (sum, item) => sum + (item.completed_quantity || 0),
        0
      );
      return {
        dateKey: dk,
        totalAssigned,
        totalCompleted,
        totalScrapped,
        freshAssigned,
        freshCompleted,
        isSettled: true,
      };
    }

    let totalAssigned = 0;
    let totalCompleted = 0;
    let totalScrapped = 0;
    rows.forEach((row) => {
      totalAssigned += row.dateMap[dk] || 0;
      totalCompleted += row.completedMap[dk] || 0;
      totalScrapped += row.scrappedMap[dk] || 0;
    });
    return {
      dateKey: dk,
      totalAssigned,
      totalCompleted,
      totalScrapped,
      isSettled: false,
    };
  });
}

export function getExportTitle(startDate, endDate) {
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  let rangeText;
  if (sameMonth) {
    rangeText = format(startDate, 'yyyy年M月');
  } else if (sameYear) {
    rangeText = `${format(startDate, 'yyyy年M月d日')}-${format(endDate, 'M月d日')}`;
  } else {
    rangeText = `${format(startDate, 'yyyy年M月d日')}-${format(endDate, 'yyyy年M月d日')}`;
  }

  return `${rangeText}生产日计划与完成统计表`;
}

export async function captureScheduleImage(container, filename) {
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
  });

  const link = document.createElement('a');
  link.download = filename || '生产日计划.png';
  link.href = canvas.toDataURL('image/png');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
